import React, { useState, useRef, ChangeEvent, useContext } from "react";
import { host } from "../../config";
import ReCAPTCHA from "react-google-recaptcha";
import "./claimsUpload.css";
import CustomAlert from "components/generals/alert/CustomAlert";
import { Button, CircularProgress, InputAdornment, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import InfoIcon from "@mui/icons-material/Info";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../authProvider/authProvider";
import { getAuth, signOut } from "firebase/auth";
import isEmail from "validator/lib/isEmail";

type NewClaimUserFormData = {
  lastName: string;
  firstName: string;
  groupNumber: string;
  memberNumber: string;
  dob: string;
  email: string;
  files?: File[];
  claimType: string;
  paymentDestination: string;
};

type PreviouslySubmittedUserFormData = {
  memberNumber: string;
  claimId: string;
  files?: File[];
  claimType: string;
  email: string;
};

const auth = getAuth();

const App: React.FC = () => {
  const [isPreviouslySubmittedClaim, setIsPreviouslySubmittedClaim] = useState<string>("");
  const [newClaimUserFormData, setNewClaimUserFormData] = useState<NewClaimUserFormData>({
    lastName: "",
    firstName: "",
    groupNumber: "",
    memberNumber: "",
    dob: "",
    email: "",
    claimType: "",
    paymentDestination: "",
  });
  const [previouslySubmittedClaimUserFormData, setPreviouslySubmittedClaimUserFormData] =
    useState<PreviouslySubmittedUserFormData>({
      memberNumber: "",
      claimId: "",
      claimType: "",
      email: "",
    });
  interface Files {
    [key: string]: File;
  }

  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [files, setFiles] = useState<Files>({});

  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const closeAlert = () => {
    setAlertOpen(false);
  };

  const [isLoading, setIsLoading] = useState(false);
  const captchaRef = useRef<ReCAPTCHA | null>(null);

  const navigate = useNavigate(); // Hook to navigate between routes

  const authContext = useContext(AuthContext); // Get Auth context
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }

  const handleSectionSelection = (section: string) => {
    setSelectedSections((prevSections) =>
      prevSections.includes(section) ? prevSections.filter((sec) => sec !== section) : [...prevSections, section]
    );
  };

  const handleFormTypeChange = (formType: string) => {
    setIsPreviouslySubmittedClaim(formType);
    setFiles({});
  };

  const handleFileChange = (name: string) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : new File([], "");
    if (file.name.match(/[\/\\\*<>\?:\|#%]/g)) {
      event.target.value = "";
      setAlertMessage(
        `File name contains restricted characters. Please remove the following from the file name. "/" / "\" / "*" / "<" / ">" / "?" / ":" / "|" / "#" / "%"`
      );
      setAlertOpen(true);
    } else {
      setFiles((prevFiles) => ({
        ...prevFiles,
        [name]: file,
      }));
    }
  };

  const formFileLabels: [string, { label: string; desc: string }[]][] = [
    [
      "GAP Supplemental",
      [
        {
          label: "Claim Form",
          desc: "The Claim Form can be either from your provider OR from your hospital or outpatient facility. We need this information to confirm the amounts charged to your Major Medical Plan.",
        },
        {
          label: "Copy of your Explanation of Benefits",
          desc: "This form allows us to determine the benefit amount we will pay through your GAP plan. Please note that without a copy of your explanation of benefits we will not be able to determine any reimbursement amount for your claim.",
        },
      ],
    ],
    [
      "Minimum Essential Coverage (MEC)",
      [
        {
          label: "Claim Form",
          desc: "The Claim Form can be either from your provider OR from your hospital or outpatient facility.",
        },
      ],
    ],
    [
      "Limited Medical",
      [
        {
          label: "Claim Form",
          desc: "The Claim Form can be either from your provider OR from your hospital or outpatient facility.",
        },
      ],
    ],
    [
      "RX",
      [
        {
          label: "Copy of your Explanation of Benefits",
          desc: "This form allows us to determine the benefit amount we will pay through your GAP plan. Please note that without a copy of your explanation of benefits we will not be able to determine any reimbursement amount for your claim.",
        },
        {
          label: "Pharmacy Pamphlet",
          desc: "The Pharmacy Pamphlet will be provided by the pharmacy when picking up your prescription. We need this information to confirm the amounts charged to your Major Medical Plan.",
        },
      ],
    ],
  ];

  const handleNewClaimSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    let formData = new FormData();
    Object.entries(files).forEach(([name, file]) => {
      formData.append("files", file, `${file.name.split(".").slice(0, -1)}_${name}.${file.name.split(".").slice(-1)}`);
    });
    formData.append("body", JSON.stringify({ ...newClaimUserFormData, recaptchaToken: recaptchaValue }));
    var counter = 0;
    for (let file of formData.getAll("files")) {
      counter++;
    }
    if (!recaptchaValue) {
      setAlertMessage("Please verify you are not a robot.");
      setAlertOpen(true);
      return;
    } else if (counter == 0) {
      setAlertMessage("Please upload at least one file");
      setAlertOpen(true);
      return;
    } else if (
      Number(newClaimUserFormData.groupNumber) <= 200000 &&
      newClaimUserFormData.groupNumber !== "04004" &&
      newClaimUserFormData.groupNumber !== "" &&
      newClaimUserFormData.groupNumber !== "000001"
    ) {
      setAlertMessage("This feature is not supported for your group. We apologize for the inconvience.");
      setAlertOpen(true);
      return;
    } else if (!isEmail(newClaimUserFormData.email)) {
      setAlertMessage("Please enter a valid email address.");
      setAlertOpen(true);
      return;
    }
    setIsLoading(true);
    console.log(formData);
    console.log(`${host}/api/claims/vba/newClaimUpload`);
    fetch(`${host}/api/claims/vba/newClaimUpload`, {
      method: "POST",
      body: formData,
    })
      .then((response: Response) => {
        if (!response.ok) {
          response.json().then((res) => {
            if (
              res.error ===
              `File name contains restricted characters. Please remove the following from the file name. "/" / "\\" / "*" / "<" / ">" / "?" / ":" / "|" / "#" / "%"`
            ) {
              setAlertMessage(
                `File name contains restricted characters. Please remove the following from the file name. "/" / "\\" / "*" / "<" / ">" / "?" / ":" / "|" / "#" / "%"`
              );
              setAlertOpen(true);
            } else {
              setAlertMessage("An error has occured. Please try again later or contact support if the issue persists.");
              setAlertOpen(true);
            }
          });
        } else {
          response.json().then((res) => {
            setIsLoading(false);
            if (res.error) {
              setAlertMessage(res.error);
            } else if (res.total_claims_failed && res.total_claims_failed > 0) {
              setAlertMessage(
                `We could not accept you claim at this time. Please try again later or contact your account manager is the issue persists.`
              );
            } else {
              setAlertMessage(
                `Success! Thank you for submitting your claim online. Your claim has been submitted, and you will receive a confirmation email regarding this submission. Please allow 10-15 business days for processing of your claim. Should you have any questions, please contact us at (800)-836-6036`
              );
            }
            setAlertOpen(true);
          });
          setNewClaimUserFormData({
            lastName: "",
            firstName: "",
            groupNumber: "",
            memberNumber: "",
            dob: "",
            email: "",
            claimType: "",
            paymentDestination: "",
          });
          setFiles({});
          captchaRef.current?.reset();
          setRecaptchaValue(null);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        // alert("An error occurred: " + error.message);
        setAlertMessage("An error occurred: " + error.message);
        setAlertOpen(true);
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        console.log("Processing finished");
      });
  };
  const handlePreviouslyUploadedClaimSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    let formData = new FormData();
    Object.entries(files).forEach(([name, file]) => {
      formData.append("files", file, `${file.name.split(".").slice(0, -1)}_${name}.${file.name.split(".").slice(-1)}`);
    });
    formData.append(
      "body",
      JSON.stringify({ ...previouslySubmittedClaimUserFormData, recaptchaToken: recaptchaValue })
    );
    var counter = 0;
    for (let file of formData.getAll("files")) {
      counter++;
    }
    if (!recaptchaValue) {
      setAlertMessage("Please verify you are not a robot.");
      setAlertOpen(true);
      return;
    } else if (counter == 0) {
      setAlertMessage("Please upload at least one file");
      setAlertOpen(true);
      return;
    } else if (!isEmail(previouslySubmittedClaimUserFormData.email)) {
      setAlertMessage("Please enter a valid email address.");
      setAlertOpen(true);
      return;
    }
    setIsLoading(true);
    console.log(formData);
    console.log(`${host}/api/claims/vba/previouslySubmittedClaimUpload`);
    fetch(`${host}/api/claims/vba/previouslySubmittedClaimUpload`, {
      method: "POST",
      body: formData,
    })
      .then((response: Response) => {
        if (!response.ok) {
          response.json().then((res) => {
            if (
              res.error ===
              `File name contains restricted characters. Please remove the following from the file name. "/" / "\\" / "*" / "<" / ">" / "?" / ":" / "|" / "#" / "%"`
            ) {
              setAlertMessage(
                `File name contains restricted characters. Please remove the following from the file name. "/" / "\\" / "*" / "<" / ">" / "?" / ":" / "|" / "#" / "%"`
              );
              setAlertOpen(true);
            } else {
              setAlertMessage("An error has occured. Please try again later or contact support if the issue persists.");
              setAlertOpen(true);
            }
          });
        } else {
          response.json().then((res) => {
            setIsLoading(false);
            if (res.error) {
              setAlertMessage(
                `We could not accept you claim at this time. Please try again later or contact your account manager is the issue persists.`
              );
            } else {
              setAlertMessage(
                `Success! Thank you for submitting your claim online. Your claim has been submitted, and you will receive a confirmation email regarding this submission. Please allow 10-15 business days for processing of your claim. Should you have any questions, please contact us at (800)-836-6036`
              );
            }
            setAlertOpen(true);
          });
          setPreviouslySubmittedClaimUserFormData({
            memberNumber: "",
            claimId: "",
            claimType: "",
            email: "",
          });
          setFiles({});
          captchaRef.current?.reset();
          setRecaptchaValue(null);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        // alert("An error occurred: " + error.message);
        setAlertMessage("An error occurred: " + error.message);
        setAlertOpen(true);
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        console.log("Processing finished");
      });
  };

  const onChange = (value: any) => {
    console.log("Captcha value:", value);
    setRecaptchaValue(value);
  };

  const handleMouseOver = (e: React.MouseEvent<HTMLDivElement>, desc: string) => {
    const tooltip = document.getElementById(`tooltip_${desc}`);
    if (tooltip) {
      tooltip.style.left = e.clientX + "px";
      tooltip.style.top = e.clientY + "px";
      tooltip.innerHTML = `Upload a file for ${desc}`;
      tooltip.style.visibility = "visible";
      tooltip.style.opacity = "1";
    }
  };

  const handleMouseLeave = (desc: string) => {
    const tooltip = document.getElementById(`tooltip_${desc}`);
    if (tooltip) {
      tooltip.style.visibility = "hidden";
      tooltip.style.opacity = "0";
    }
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }
  return (
    <div className="claim-submission-container">
      <CustomAlert message={alertMessage} isOpen={isAlertOpen} onClose={closeAlert} />
      <h1 className="claim-submission-title">Online Claim Submission</h1>

      <form className="previously-submitted-claim-div">
        <Grid container direction="row" spacing={1}>
          <Grid
            container
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ marginBottom: "0.75rem" }}
          >
            <Grid item xs>
              <label className="claim-submission-label">
                Is this a previously submitted claim?
                <Select
                  required={true}
                  //placeholder="None"
                  className="claim-submission-select"
                  name="claimType"
                  value={isPreviouslySubmittedClaim}
                  onChange={(e) => setIsPreviouslySubmittedClaim(e.target.value)}
                >
                  <MenuItem disabled value="">
                    Select...
                  </MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </label>
            </Grid>
          </Grid>
        </Grid>
      </form>
      {isPreviouslySubmittedClaim === "Yes" && (
        <>
          <p className="claim-submission-supporting-text">
            Please enter the information below as it appears on your member ID card. Please note all fields are required
            in order to submit your claims online.
          </p>
          <form onSubmit={handlePreviouslyUploadedClaimSubmit} className="claim-submission-form">
            <Grid container direction="row" spacing={1}>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label>
                    9-Digit Subscriber ID
                    <TextField
                      required={true}
                      type="text"
                      placeholder="Patient Member Number"
                      className="claim-submission-input"
                      value={previouslySubmittedClaimUserFormData.memberNumber}
                      onChange={(e) =>
                        setPreviouslySubmittedClaimUserFormData({
                          ...previouslySubmittedClaimUserFormData,
                          memberNumber: e.target.value,
                        })
                      }
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Please enter the members 9 digit Member ID. If you have a 12 digit member ID please contact claims@optimedhealth.com.">
                            <InputAdornment position="end">
                              <InfoIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </label>
                </Grid>
                <Grid item xs>
                  <label>
                    Claim ID
                    <TextField
                      required={true}
                      type="text"
                      placeholder="Claim ID"
                      className="claim-submission-input"
                      value={previouslySubmittedClaimUserFormData.claimId}
                      onChange={(e) =>
                        setPreviouslySubmittedClaimUserFormData({
                          ...previouslySubmittedClaimUserFormData,
                          claimId: e.target.value,
                        })
                      }
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Please enter the Claim ID for the previously submitted claim.">
                            <InputAdornment position="end">
                              <InfoIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label>
                    Email
                    <TextField
                      required={true}
                      type="email"
                      placeholder="Email"
                      className="claim-submission-input"
                      value={previouslySubmittedClaimUserFormData.email}
                      onChange={(e) =>
                        setPreviouslySubmittedClaimUserFormData({
                          ...previouslySubmittedClaimUserFormData,
                          email: e.target.value,
                        })
                      }
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="We will provide updates regarding the claim status when we are given a valid email.">
                            <InputAdornment position="end">
                              <InfoIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <div>
                    <label>
                      Please select the product you are submitting a claim for. You will be required to supply
                      supporting documentation for your claim, prior to submitting.
                    </label>
                    <div className="section-buttons">
                      {formFileLabels.map(([section]) => (
                        <button
                          key={section}
                          type="button"
                          onClick={() => handleSectionSelection(section)}
                          style={{
                            backgroundColor: selectedSections.includes(section) ? "#FF8C00" : "#004e9d",
                            color: "white",
                            margin: "5px",
                          }}
                        >
                          {section.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <div className="upload-components">
                    {formFileLabels.map(([formTitle, fileLabels]) => {
                      if (selectedSections.includes(formTitle)) {
                        return (
                          <div className="upload-form" key={formTitle}>
                            <h2>{formTitle}</h2>
                            {fileLabels.map(({ label, desc }) => (
                              <div className="form-field" key={label}>
                                <div className="label-info-wrapper">
                                  <label>{label}</label>
                                  <div
                                    className="info-bubble"
                                    onMouseOver={(e) => handleMouseOver(e, desc)}
                                    onMouseLeave={() => handleMouseLeave(desc)}
                                  >
                                    i
                                  </div>
                                </div>
                                <input type="file" onChange={handleFileChange(label)} accept="image/*,.pdf" />
                                <div className="tooltip-text" id={`tooltip_${desc}`}></div>
                              </div>
                            ))}
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <div className="claim-submission-captcha">
                    <ReCAPTCHA
                      sitekey="6Lftt0EoAAAAAHah4Wv3DZVjYiacRU7xcVqhzm9U" // Use environment variable
                      onChange={onChange}
                      ref={captchaRef}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <Button type="submit" className="claim-submission-button" variant="contained">
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </>
      )}
      {isPreviouslySubmittedClaim === "No" && (
        <>
          <p className="claim-submission-supporting-text">
            Please enter the information below as it appears on your member ID card. Please note all fields are required
            in order to submit your claims online.
          </p>
          <form onSubmit={handleNewClaimSubmit} className="claim-submission-form">
            <Grid container direction="row" spacing={1}>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label>
                    Patient Last Name
                    <TextField
                      required={true}
                      type="text"
                      placeholder="Patient Last Name"
                      className="claim-submission-input"
                      value={newClaimUserFormData.lastName}
                      onChange={(e) => setNewClaimUserFormData({ ...newClaimUserFormData, lastName: e.target.value })}
                    />
                  </label>
                </Grid>
                <Grid item xs>
                  <label>
                    Patient First Name
                    <TextField
                      required={true}
                      type="text"
                      placeholder="Patient First Name"
                      className="claim-submission-input"
                      value={newClaimUserFormData.firstName}
                      onChange={(e) => setNewClaimUserFormData({ ...newClaimUserFormData, firstName: e.target.value })}
                    />
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label>
                    Patient Date Of Birth
                    <TextField
                      required={true}
                      type="date"
                      className="claim-submission-date-input"
                      value={newClaimUserFormData.dob}
                      onChange={(e) => setNewClaimUserFormData({ ...newClaimUserFormData, dob: e.target.value })}
                    />
                  </label>
                </Grid>
                <Grid item xs>
                  <label>
                    9-Digit Subscriber ID
                    <TextField
                      required={true}
                      type="text"
                      placeholder="Patient Member Number"
                      className="claim-submission-input"
                      value={newClaimUserFormData.memberNumber}
                      onChange={(e) =>
                        setNewClaimUserFormData({ ...newClaimUserFormData, memberNumber: e.target.value })
                      }
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Please enter the members 9 digit Member ID. If you have a 12 digit member ID please contact claims@optimedhealth.com.">
                            <InputAdornment position="end">
                              <InfoIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </label>
                </Grid>
                <Grid item xs>
                  <label>
                    Patient Group Number
                    <TextField
                      required={true}
                      type="text"
                      placeholder="Patient Group Number"
                      className="claim-submission-input"
                      value={newClaimUserFormData.groupNumber}
                      onChange={(e) =>
                        setNewClaimUserFormData({ ...newClaimUserFormData, groupNumber: e.target.value })
                      }
                    />
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label>
                    Email
                    <TextField
                      required={true}
                      type="email"
                      placeholder="Email"
                      className="claim-submission-input"
                      value={newClaimUserFormData.email}
                      onChange={(e) => setNewClaimUserFormData({ ...newClaimUserFormData, email: e.target.value })}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="We will provide updates regarding the claim status when we are given a valid email.">
                            <InputAdornment position="end">
                              <InfoIcon style={{ color: "rgba(0, 0, 0, 0.54)" }} />
                            </InputAdornment>
                          </Tooltip>
                        ),
                      }}
                    />
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label className="claim-submission-label">
                    {" "}
                    Please select where the patient received services
                    <Select
                      required={true}
                      //placeholder="None"
                      className="claim-submission-select"
                      name="claimType"
                      value={newClaimUserFormData.claimType}
                      onChange={(e) => setNewClaimUserFormData({ ...newClaimUserFormData, claimType: e.target.value })}
                    >
                      <MenuItem disabled value="">
                        Select...
                      </MenuItem>
                      <MenuItem value="PROF">Doctors Office</MenuItem>
                      <MenuItem value="UB92">Hospital</MenuItem>
                      <MenuItem value="RX">Pharmacy</MenuItem>
                      <MenuItem value="Neither">Neither</MenuItem>
                    </Select>
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <label className="claim-submission-label">
                    {" "}
                    Please select if the member or provider should receive payment
                    <Select
                      required={true}
                      // placeholder="None"
                      className="claim-submission-select"
                      name="paymentDestination"
                      value={newClaimUserFormData.paymentDestination}
                      onChange={(e) =>
                        setNewClaimUserFormData({ ...newClaimUserFormData, paymentDestination: e.target.value })
                      }
                    >
                      <MenuItem disabled value="">
                        Select...
                      </MenuItem>
                      <MenuItem value="Member">Member</MenuItem>
                      <MenuItem value="Provider">Provider</MenuItem>
                    </Select>
                  </label>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <div>
                    <label>
                      Please select the product you are submitting a claim for. You will be required to supply
                      supporting documentation for your claim, prior to submitting.
                    </label>
                    <div className="section-buttons">
                      {formFileLabels.map(([section]) => (
                        <button
                          key={section}
                          type="button"
                          onClick={() => handleSectionSelection(section)}
                          style={{
                            backgroundColor: selectedSections.includes(section) ? "#FF8C00" : "#004e9d",
                            color: "white",
                            margin: "5px",
                          }}
                        >
                          {section.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <div className="upload-components">
                    {formFileLabels.map(([formTitle, fileLabels]) => {
                      if (selectedSections.includes(formTitle)) {
                        return (
                          <div className="upload-form" key={formTitle}>
                            <h2>{formTitle}</h2>
                            {fileLabels.map(({ label, desc }) => (
                              <div className="form-field" key={label}>
                                <div className="label-info-wrapper">
                                  <label>{label}</label>
                                  <div
                                    className="info-bubble"
                                    onMouseOver={(e) => handleMouseOver(e, desc)}
                                    onMouseLeave={() => handleMouseLeave(desc)}
                                  >
                                    i
                                  </div>
                                </div>
                                <input type="file" onChange={handleFileChange(label)} accept="image/*,.pdf" />
                                <div className="tooltip-text" id={`tooltip_${desc}`}></div>
                              </div>
                            ))}
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <div className="claim-submission-captcha">
                    <ReCAPTCHA
                      sitekey="6Lftt0EoAAAAAHah4Wv3DZVjYiacRU7xcVqhzm9U" // Use environment variable
                      onChange={onChange}
                      ref={captchaRef}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ marginBottom: "0.75rem" }}
              >
                <Grid item xs>
                  <Button type="submit" className="claim-submission-button" variant="contained">
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </>
      )}
    </div>
  );
};

export default App;
